import {LoadingSpinner, utils} from '@wallaby-ui/core';
import classNames from 'classnames';
import {isEmpty} from 'lodash';
import * as React from 'react';
import {useState} from 'react';
import {Collapse, OverlayTrigger, Tooltip} from 'react-bootstrap';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';

import FilterNodeTick from './FilterNodeTick';
import UserFilter from './UserFilter';
import styles from './UserFilters.less';
import PropertiesGroup from 'src/js/containers/document/components/PropertiesGroup';
import {FilterUtils} from 'src/js/lib/filtering';
import {FilterSchema} from 'src/js/model/filters_config';
import {UserFilters as UserFiltersModel} from 'src/js/model/user_filters';

const I18N = defineMessages({
  SAVED_FILTERS: {
    id: 'filter.user.savedFilters',
    defaultMessage: 'Saved filters',
    description: 'header text for user filters',
  },
  EMPTY: {
    id: 'filter.user.empty',
    defaultMessage: 'No saved user filters',
    description: 'text that is displayed when user filters are empty',
  },
  DEFAULT_FILTER: {
    id: 'filter.user.default',
    defaultMessage: 'Default',
  },
  DEFAULT_FILTER_TOOLTIP: {
    id: 'filter.user.default.tooltip',
    defaultMessage:
      'Here you can set a saved filter as default to apply automatically.',
  },
});

interface OwnProps {
  autocompleteValues: Record<string, any>;
  hierarchyChildrenLabels?: Record<string, any>;
  removeFilter: (filter: any) => void;
  setDefaultFilter: (filter: any) => void;
  schema: FilterSchema;
  useFilter: (filter: any) => void;
  userFilters: UserFiltersModel | Record<string, any>;
  userFiltersLoading: number;
}

type Props = OwnProps;

export const UserFilters: React.FC<Props> = (props) => {
  const {
    autocompleteValues,
    hierarchyChildrenLabels,
    removeFilter,
    setDefaultFilter,
    schema,
    useFilter,
    userFilters,
    userFiltersLoading,
  } = props;
  const [isOpened, setIsOpened] = useState<boolean>(true);
  const intl = useIntl();

  const toggleOpened = () => {
    setIsOpened(!isOpened);
  };

  return (
    <div className={styles.dlUserFilters}>
      <PropertiesGroup
        title={utils.TranslateUtils.translate(I18N.SAVED_FILTERS, intl)}
      >
        {!isEmpty(userFilters) && userFilters.getFilters().length > 0 ? (
          <div>
            <div className={styles.dlUserFiltersHeaderRow}>
              <FilterNodeTick isOpened={isOpened} toggleOpened={toggleOpened} />
              {isOpened && (
                <span className={styles.dlUserFiltersDefaultHeader}>
                  <span className={styles.dlUserFiltersDefault}>
                    {utils.TranslateUtils.translate(I18N.DEFAULT_FILTER, intl)}
                  </span>
                  <OverlayTrigger
                    placement={'top'}
                    overlay={
                      <Tooltip id={'tooltip-top'}>
                        {utils.TranslateUtils.translate(
                          {...I18N.DEFAULT_FILTER_TOOLTIP},
                          intl
                        )}
                      </Tooltip>
                    }
                  >
                    <i className={'fas fa-info-circle'} />
                  </OverlayTrigger>
                </span>
              )}
            </div>
            <Collapse in={isOpened}>
              <div
                className={classNames(
                  styles.dlUserFiltersContent,
                  'dlLoadingSpinnerContainerOverflowed',
                  {dlLoadingSpinnerParent: userFiltersLoading > 0}
                )}
              >
                <LoadingSpinner isVisible={userFiltersLoading > 0} />
                {userFilters.getFilters().map((filter) => (
                  <UserFilter
                    componentString={FilterUtils.fromStringToComponentString(
                      filter.getValue(),
                      schema,
                      intl,
                      {
                        hierarchyChildrenLabels,
                        // TODO: handle organizations and enums
                        autocompleteValues,
                        dataSets,
                      }
                    )}
                    key={filter.getName()}
                    filter={filter}
                    removeFilter={removeFilter}
                    useFilter={useFilter}
                    setDefaultFilter={setDefaultFilter}
                  />
                ))}
              </div>
            </Collapse>
          </div>
        ) : (
          <span className={styles.dlUserFiltersEmpty}>
            <FormattedMessage {...I18N.EMPTY} />
          </span>
        )}
      </PropertiesGroup>
    </div>
  );
};

UserFilters.displayName = 'UserFilters';
